import { AddressDisplay, SetFieldValue, Typeahead } from '../../gs-component-form';
import { RouterContext } from '../../gs-component-right-rail';
import { FormBlockColumn } from '@xo-union/tk-component-form-grid';
import { Field as FormikField } from 'formik';
import React, { useContext } from 'react';

import { ApiClientsContext } from '../components/ApiClientsContext';
import { LocationContext } from '../components/LocationContext';
import RightRailSection from '../components/RightRailSection';
import { OptionalForm } from '../components/Form';
import addressComponentParser, { bookingType, LocalVendorValues } from '../helpers/addressComponentHelper';
import hasAddress from '../helpers/addressHelpers';
import resetVendorFields from '../helpers/formHelpers';
import { GoogleAddressValues } from '../helpers/objectifyGoogleAddressComponents';
import { EventInfoForm } from '../ScheduleOfEvents/EventInfoForm';
import { FormValues } from './RightRailForm';

export interface Props {
  setFieldValue: SetFieldValue;
  values: FormValues;
}

export const DetailsForm: React.FC<Props> = ({ setFieldValue, values }) => {
  const { setCeremonyAddress, setReceptionAddress, location, deleteBooking } = useContext(LocationContext);
  const { eventType: type } = useContext(ApiClientsContext);
  const { push } = useContext(RouterContext);

  const deleteEventLocation = async (): Promise<void> => {
    if (type === 'Ceremony') {
      deleteBooking(type);
    }
    resetVendorFields(setFieldValue, 0);
  };

  const editEventLocation = (): void => {
    push({ screen: 'addCustomVendor', params: { eventIndex: 0, eventType: 'Ceremony' } });
  };

  const editLocationCallback = (): undefined | Function => {
    if (!location || !location.CER || bookingType(location.CER.address_components) !== 'user_entry') {
      return undefined;
    }
    return editEventLocation;
  };

  const onChange = (addressComponentValues: GoogleAddressValues | LocalVendorValues, source: string): void => {
    const { vendor_profile_id, vendor_name, gdsAddress, address_components } = addressComponentParser(
      addressComponentValues,
      source
    );

    setFieldValue(`events[0].location.zip`, gdsAddress.zip);
    setFieldValue(`events[0].location.city`, gdsAddress.city);
    setFieldValue(`events[0].location.state`, gdsAddress.state);
    setFieldValue(`events[0].location.country`, gdsAddress.country);
    setFieldValue(`events[0].location.street1`, gdsAddress.street1);
    setFieldValue(`events[0].location.name`, vendor_name);

    if (type === 'Ceremony') {
      setCeremonyAddress(
        {
          id: location.CER && location.CER.id,
          vendor_profile_id,
          vendor_name,
          address_components,
        },
        values.events[0].sameVenue
      );
    }

    if (values.events[0].sameVenue) {
      setFieldValue('events[1].location.zip', gdsAddress.zip);
      setFieldValue('events[1].location.city', gdsAddress.city);
      setFieldValue('events[1].location.state', gdsAddress.state);
      setFieldValue('events[1].location.country', gdsAddress.country);
      setFieldValue('events[1].location.street1', gdsAddress.street1);
      setFieldValue('events[1].location.name', vendor_name);
      setReceptionAddress({
        id: location.REC && location.REC.id,
        vendor_profile_id,
        vendor_name,
        address_components,
      });
    }
  };

  return (
    <RightRailSection name={type === 'Ceremony' ? 'The Wedding Event Information' : 'event information'}>
      <EventInfoForm setFieldValue={setFieldValue} namePrefix="events[0]." showDate autoFocusField={undefined} />
      {hasAddress(values.events[0].location) ? (
        <FormBlockColumn>
          <AddressDisplay
            vendorType="Venue Name"
            deleteCallback={deleteEventLocation}
            editCallback={editLocationCallback()}
            {...values.events[0].location}
          />
        </FormBlockColumn>
      ) : (
        <FormBlockColumn>
          <FormikField
            name="events[0].location.name"
            label="Venue Name"
            onChange={onChange}
            component={Typeahead}
            onClickAddCustom={editEventLocation}
            allowCustomVendor
          />
        </FormBlockColumn>
      )}
      <OptionalForm namePrefix="events[0]." />
    </RightRailSection>
  );
};
